import { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { LoadingScreen } from '@/components/ui/LoadingScreen'
import type { UserPrimaryRole } from '@/types'

const ROLE_HOME: Record<UserPrimaryRole, string> = {
  school_admin: '/app/school',
  instructor:   '/app/instructor',
  student:      '/app/student',
}

export default function AuthCallback() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  useEffect(() => {
    const handle = async () => {
      // Force a server-side user fetch to bypass any stale localStorage session.
      // This is critical after account deletion + re-signup with the same provider.
      const { data: { user }, error: userError } = await supabase.auth.getUser()

      if (userError || !user) {
        // Clear any stale session and retry via login
        await supabase.auth.signOut()
        navigate('/login?error=auth_callback_failed', { replace: true })
        return
      }

      const { data: { session }, error } = await supabase.auth.getSession()
      if (error || !session) {
        navigate('/login?error=auth_callback_failed', { replace: true })
        return
      }

      // Check if the user already has a profile with a role
      const { data: profile } = await supabase
        .from('profiles')
        .select('primary_role')
        .eq('id', session.user.id)
        .single()

      if (profile?.primary_role) {
        navigate(ROLE_HOME[profile.primary_role as UserPrimaryRole], { replace: true })
      } else {
        // New OAuth user or user without a role — send to SelectRole
        const hintedRole = searchParams.get('role') ?? ''
        const destination = hintedRole
          ? `/auth/select-role?role=${hintedRole}`
          : '/auth/select-role'
        navigate(destination, { replace: true })
      }
    }

    handle()
  }, [navigate, searchParams])

  return <LoadingScreen />
}
